import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  ChevronDown,
  Plus,
  Minus,
  Download,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  Pencil,
  Trash2,
  Settings,
  X,
  AlertTriangle,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { DevicesContext } from '../../contexts/DevicesContext';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import { useAuth } from '../../hooks/useAuth';
import DeviceOffSets from './DeviceOffSets';
import DeviceConfigFlowMobile from './DeviceConfigFlowMobile';
import DeviceConfigFlowWeb from './configFlowWeb/DeviceConfigFlowWeb';
import { isRNApp } from '../../utils/helperFunctions';
import '../../styles/pages/DeviceDetailPage.css';
import {
  DeviceDashboardDto,
  DeviceReading,
  ParameterDtoPreferred,
} from '../../services/deviceApi';
import HelperFunctions, {
  timeRanges,
  options,
  ExportDataTypes,
  getDeviceStatusLabel,
} from '../../utils/helperFunctions';
import {
  getUserTimezone,
  getDifferenceInHours,
  getLocalDateTimeString,
} from '../../utils/timezoneUtils';
import isEqual from 'lodash/isEqual';
import DeviceHeatMap from './DeviceHeatMap';
import AQILearningModal from '../../components/modals/AQILearningModal';
import DeleteDeviceModal from '../../components/modals/DeleteDeviceModal';

// ─── Parameter categorisation ────────────────────────────────────────────────
// Matches against ParameterDto.parameterName (not display name)
const BASIC_KEYS = ['AQI', 'PM2.5', 'PM10', 'CO', 'O₃', 'NO₂', 'SO₂', 'Light'];
const POLLUTANT_KEYS = ['CO2', 'CO₂', 'TVOC', 'VOC', 'CH₂O', 'Ozone', 'Viral'];
const ENV_KEYS = ['Temperature', 'Humidity', 'Barometric', 'Noise'];
const HEALTH_KEYS = ['Mold', 'Virus'];

type ParamCategory = 'basic' | 'pollutant' | 'environmental' | 'health';

function classifyParam(name: string): ParamCategory {
  const n = name.toLowerCase();
  if (BASIC_KEYS.some(k => n.includes(k.toLowerCase()))) return 'basic';
  if (POLLUTANT_KEYS.some(k => n.toLowerCase() === k.toLowerCase() || n.includes(k.toLowerCase()))) return 'pollutant';
  if (ENV_KEYS.some(k => n.includes(k.toLowerCase()))) return 'environmental';
  if (HEALTH_KEYS.some(k => n.includes(k.toLowerCase()))) return 'health';
  return 'pollutant'; // fallback to other pollutants
}

// ─── Environment mode tabs ────────────────────────────────────────────────────
const ENV_MODES = ['Kids', 'Elderly', 'Sensitive', 'General', 'Workspace'];

// Map env modes to icon files in public/SVGIcons. Keep these in sync with the downloaded SVG frames.
const ENV_MODE_ICONS: Record<string, string> = {
  Kids: `${process.env.PUBLIC_URL}/SVGIcons/Frame 2147223849.svg`,
  Elderly: `${process.env.PUBLIC_URL}/SVGIcons/Frame 2147223855.svg`,
  Sensitive: `${process.env.PUBLIC_URL}/SVGIcons/Frame 2147223853.svg`,
  General: `${process.env.PUBLIC_URL}/SVGIcons/Frame 2147223854.svg`,
  Workspace: `${process.env.PUBLIC_URL}/SVGIcons/Frame 2147223846.svg`,
};

// ─── Status color helper ──────────────────────────────────────────────────────
function statusTextColor(status: string): string {
  switch (status) {
    case 'Good': return 'text-green-500 dark:text-green-400';
    case 'Moderate': return 'text-yellow-500 dark:text-yellow-400';
    case 'Unhealthy for Sensitive Groups': return 'text-orange-500 dark:text-orange-400';
    case 'Unhealthy': return 'text-red-500 dark:text-red-400';
    case 'Very Unhealthy': return 'text-purple-500 dark:text-purple-400';
    case 'Hazardous': return 'text-pink-700 dark:text-pink-500';
    default: return 'text-gray-400 dark:text-gray-500';
  }
}

function statusBgColor(status: string): string {
  switch (status) {
    case 'Good': return '#A0ED9F';
    case 'Moderate': return '#FFE48A';
    case 'Unhealthy for Sensitive Groups': return '#FFB36E';
    case 'Unhealthy': return '#FF8880';
    case 'Very Unhealthy': return '#D5A1FE';
    case 'Hazardous': return '#CB89C4';
    default: return '#d6d4d0';
  }
}

// ─── Collapsible section component ───────────────────────────────────────────
interface CollapsibleSectionProps {
  title: string;
  titleColor?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  titleColor = 'text-orange-500',
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <button
        className="w-full flex items-center justify-between py-3 px-1 group"
        onClick={() => setOpen(o => !o)}
      >
        <span className={`text-xs font-semibold uppercase tracking-wide ${titleColor}`}>
          {title}
        </span>
        {open ? (
          <Minus className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        ) : (
          <Plus className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && <div className="pb-2">{children}</div>}
    </div>
  );
};

// ─── Single parameter row in left panel ──────────────────────────────────────
interface ParamRowProps {
  name: string;
  displayName: string;
  value: number | null;
  unit: string;
  subIndex: number | null;
  isMainPollutant?: boolean;
  isPrimarySelected?: boolean;
  isSecondarySelected?: boolean;
  onSelect?: () => void;
}
const ParamRow: React.FC<ParamRowProps> = ({
  name,
  displayName,
  value,
  unit,
  subIndex,
  isMainPollutant,
  isPrimarySelected,
  isSecondarySelected,
  onSelect,
}) => {
  const statusName =
    name === 'PM2.5' || name === 'PM10' || name === 'CO' || name === 'O₃' || name === 'NO₂'
      ? subIndex != null
        ? HelperFunctions.GetStatusText(subIndex, 'AQI')
        : HelperFunctions.GetStatusText(value ?? undefined, name)
      : HelperFunctions.GetStatusText(value ?? undefined, name);

  const formattedValue =
    value != null ? (Number.isInteger(value) ? value : value.toFixed(2)) : '--';

  const rowStateClass = isPrimarySelected
    ? 'ring-2 ring-green-500 dark:ring-green-400 bg-green-50 dark:bg-green-950/20'
    : isSecondarySelected
      ? 'ring-2 ring-blue-500 dark:ring-blue-400 bg-blue-50 dark:bg-blue-950/20'
      : 'hover:bg-gray-50 dark:hover:bg-gray-800';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left flex items-center justify-between py-2 px-1 rounded-md transition-colors ${rowStateClass}`}
    >
      <div className="flex items-center gap-1 min-w-0 flex-1">
        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
          {displayName}
        </span>
        {isMainPollutant && (
          <span className="ml-1 text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded px-1 py-0.5 whitespace-nowrap">
            Main Pollutant
          </span>
        )}
        <Info className="w-3 h-3 text-gray-400 flex-shrink-0 ml-0.5" />
      </div>
      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
        <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
          {formattedValue}
          {unit && value != null && (
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-0.5">
              {unit}
            </span>
          )}
        </span>
        <span className={`text-xs font-medium ${statusTextColor(statusName)} min-w-[60px] text-right`}>
          {statusName !== 'NA' ? statusName : '--'}
        </span>
      </div>
    </button>
  );
};

// ─── Trend icon ───────────────────────────────────────────────────────────────
function TrendIcon({ trend }: { trend: string }) {
  if (trend === 'Rising')
    return <span className="flex items-center gap-0.5 text-red-500 text-xs font-medium"><ArrowUpRight className="w-3 h-3" />Rising</span>;
  if (trend === 'Improving')
    return <span className="flex items-center gap-0.5 text-green-500 text-xs font-medium"><ArrowDownRight className="w-3 h-3" />Improving</span>;
  return <span className="flex items-center gap-0.5 text-yellow-500 text-xs font-medium"><ArrowRight className="w-3 h-3" />Stable</span>;
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const NewDeviceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // ── Contexts ──────────────────────────────────────────────────────────────
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) throw new Error('Must be used within DevicesProvider');
  const {
    devicesForDashboard,
    getDevicesForDashboard,
    parametersForDashboard,
    getParametersForDashboard,
    getAqiInfoData,
    deviceSensorData,
    getDeviceSensorData,
    exportDeviceData,
    deleteDevice,
    getDeviceOffsetsData,
  } = devicesContext;

  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) throw new Error('Must be used within UserSelectedProvider');
  const {
    userSelectedParameterId,
    setUserSelectedParameterId,
    userSelectedParameterId2,
    setUserSelectedParameterId2,
    userSelectedTimeRange,
    setUserSelectedTimeRange,
    customFromDate,
    customToDate,
    setCustomFromDate,
    setCustomToDate,
    hideBottomBar,
    showBottomBar,
    isBottomBarHidden,
  } = userSelectedContext;

  // ── User prefs ────────────────────────────────────────────────────────────
  const userPreferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  const timeZone = userPreferences.timezone || getUserTimezone();

  // ── View state ────────────────────────────────────────────────────────────
  const viewParam = searchParams.get('view') as 'overview' | 'heatmap';
  const tabParam = searchParams.get('tab') as 'currentmonth' | 'currentyear';
  const viewMode = viewParam ?? 'overview';
  const heatmapTab = tabParam ?? 'currentmonth';

  const [selectedEnvMode, setSelectedEnvMode] = useState(0); // index into ENV_MODES
  const [showAQIModal, setShowAQIModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingSuccess, setIsDeletingSuccess] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showTimeRangeDropdown, setShowTimeRangeDropdown] = useState(false);
  const [showThreshold, setShowThreshold] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [chartData, setChartData] = useState<any[]>([]);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [customDateChanged, setCustomDateChanged] = useState(false);
  const [customDateError, setCustomDateError] = useState('');
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [isUpdateOffsetsModalOpen, setIsUpdateOffsetsModalOpen] = useState(false);
  const [showConfigFlow, setShowConfigFlow] = useState(false);
  const [showWarning, setShowWarning] = useState(true);

  const isFetchingRef = useRef(false);
  const previousDeviceRef = useRef<DeviceDashboardDto | null>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const timeRangeDropdownRef = useRef<HTMLDivElement>(null);

  // ── Theme observer ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      const t = document.documentElement.getAttribute('data-theme') as 'light' | 'dark' | null;
      if (t) setTheme(t);
    };
    handler();
    const obs = new MutationObserver(handler);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  // ── Resize ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Hide bottom bar on mobile, restore on unmount so the next page keeps its footer ─
  useEffect(() => {
    if (window.innerWidth <= 768 && !isBottomBarHidden) hideBottomBar();
    return () => {
      showBottomBar();
    };
  }, []);

  // ── Load devices/params if missing ────────────────────────────────────────
  useEffect(() => {
    if (devicesForDashboard.length === 0 || parametersForDashboard.length === 0) {
      getDevicesForDashboard();
      getParametersForDashboard();
      getAqiInfoData();
    }
  }, []);

  useEffect(() => {
    if (id) getDeviceOffsetsData(id);
  }, []);

  // ── Sync URL view param ───────────────────────────────────────────────────
  useEffect(() => {
    if (!viewParam) {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set('view', 'overview');
        return p;
      }, { replace: true });
    }
  }, []);

  const toggleView = (view: 'overview' | 'heatmap') => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('view', view);
      return p;
    }, { replace: true });
  };

  const toggleTab = (tab: 'currentmonth' | 'currentyear') => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('tab', tab);
      return p;
    }, { replace: true });
  };

  // Sync default heatmap tab into URL when switching to heatmap view
  useEffect(() => {
    if (viewMode === 'heatmap' && !tabParam) {
      setSearchParams(prev => {
        const p = new URLSearchParams(prev);
        p.set('tab', 'currentmonth');
        return p;
      }, { replace: true });
    }
  }, [viewMode]);

  // Click-outside: close export dropdown on desktop
  useEffect(() => {
    if (isMobile) return;
    const handler = (e: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target as Node)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isMobile]);

  // Click-outside: close time range dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (timeRangeDropdownRef.current && !timeRangeDropdownRef.current.contains(e.target as Node)) {
        setShowTimeRangeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Device details (memoised, stable ref) ─────────────────────────────────
  const deviceDetails = useMemo(() => {
    const found = devicesForDashboard.find(d => d.deviceId === Number(id));
    if (!previousDeviceRef.current || !isEqual(previousDeviceRef.current, found)) {
      previousDeviceRef.current = found || null;
    }
    return previousDeviceRef.current;
  }, [devicesForDashboard, id]);

  // ── Main pollutant ────────────────────────────────────────────────────────
  const mainPollutantName = useMemo(() => {
    if (!deviceDetails) return '';
    const params = Object.values(deviceDetails.parameters);
    if (params.length === 0) return '';
    const max = params.reduce((a, b) => (b.subIndex > a.subIndex ? b : a));
    return max.subIndex > 0 ? max.parameterName : '';
  }, [deviceDetails]);

  // ── Parameter selection helper (max 2 params) ─────────────────────────────
  const handleParameterClick = (parameterId: number) => {
    // 1) If click primary while secondary exists, promote secondary and clear second
    if (userSelectedParameterId === parameterId && userSelectedParameterId2 !== 0) {
      setUserSelectedParameterId(userSelectedParameterId2);
      setUserSelectedParameterId2(0);
      return;
    }

    // 2) If click secondary, clear secondary
    if (userSelectedParameterId2 === parameterId) {
      setUserSelectedParameterId2(0);
      return;
    }

    // 3) No secondary yet, add (or switch to primary if none)
    if (userSelectedParameterId2 === 0) {
      if (userSelectedParameterId === 0) {
        setUserSelectedParameterId(parameterId);
      } else if (parameterId !== userSelectedParameterId) {
        setUserSelectedParameterId2(parameterId);
      }
      return;
    }

    // 4) Both selected; replace secondary and keep primary as previous secondary
    if (parameterId !== userSelectedParameterId && parameterId !== userSelectedParameterId2) {
      setUserSelectedParameterId(userSelectedParameterId2);
      setUserSelectedParameterId2(parameterId);
    }
  };

  // ── Parameters available on this device ───────────────────────────────────
  const parametersForDevice: ParameterDtoPreferred[] = useMemo(() => {
    if (!deviceDetails) return [];
    const deviceIds = Object.keys(deviceDetails.parameters);

    const unsorted = parametersForDashboard.filter(p => deviceIds.includes(p.driverID.toString()));

    const rank = (param: ParameterDtoPreferred): number => {
      if (param.parameterName === 'AQI') return 0;
      if (param.parameterName === mainPollutantName) return 1;
      if (BASIC_KEYS.some(k => param.parameterName.toLowerCase().includes(k.toLowerCase()))) return 2;
      if (POLLUTANT_KEYS.some(k => param.parameterName.toLowerCase().includes(k.toLowerCase()))) return 3;
      if (ENV_KEYS.some(k => param.parameterName.toLowerCase().includes(k.toLowerCase()))) return 4;
      if (HEALTH_KEYS.some(k => param.parameterName.toLowerCase().includes(k.toLowerCase()))) return 5;
      return 6;
    };

    return [...unsorted].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      return a.parameterName.localeCompare(b.parameterName);
    });
  }, [deviceDetails, parametersForDashboard, mainPollutantName]);

  // ── AQI value from live data ───────────────────────────────────────────────
  const aqiParam = useMemo(() => {
    if (!deviceDetails) return null;
    return Object.values(deviceDetails.parameters).find(p => p.parameterName === 'AQI') || null;
  }, [deviceDetails]);

  const aqiValue = aqiParam?.value ?? null;
  const aqiStatus = HelperFunctions.GetStatusText(aqiParam?.value, 'AQI');
  const aqiColor = HelperFunctions.GetParameterColor(aqiParam?.value, 'AQI');

  const connectivityText = useMemo(() => {
    if (!deviceDetails) return '';

    if (deviceDetails.wifiNetworkName && deviceDetails.wifiNetworkName.trim()) {
      return deviceDetails.wifiNetworkName;
    }

    const onboardingStatus =
      (deviceDetails as any).onboarding_status || deviceDetails.onboardingStatus || '';
    const operationalStatus =
      (deviceDetails as any).operational_status || deviceDetails.operationalStatus || '';

    const normalize = (s: string) => (s || '').toString().trim().toUpperCase();
    const onboarding = normalize(onboardingStatus);
    const operational = normalize(operationalStatus);

    if (onboarding) {
      if (onboarding === 'REGISTERED') return 'Not connected';
      if (['WIFICONNECTED', 'CONNECTED', 'ONLINE'].includes(onboarding)) return 'Connected';
      return onboardingStatus;
    }

    if (operational) {
      if (['ONLINE', 'CONNECTED'].includes(operational)) return 'Connected';
      if (operational === 'OFFLINE') return 'Offline';
      return operationalStatus;
    }

    return 'Unknown';
  }, [deviceDetails]);

  // ── Categorise live parameters for left panel ─────────────────────────────
  interface LiveParamEntry {
    driverID: number;
    name: string;
    displayName: string;
    unit: string;
    value: number | null;
    subIndex: number | null;
    category: ParamCategory;
  }
  const liveParams: LiveParamEntry[] = useMemo(() => {
    if (!deviceDetails) return [];
    return parametersForDevice.map(p => {
      const live = deviceDetails.parameters[p.driverID];
      return {
        driverID: p.driverID,
        name: p.parameterName,
        displayName: p.parameterDisplayName || p.parameterName,
        unit: p.unit || '',
        value: live?.value ?? null,
        subIndex: live?.subIndex ?? null,
        category: classifyParam(p.parameterName),
      };
    });
  }, [deviceDetails, parametersForDevice]);

  const basicParams = liveParams.filter(p => p.category === 'basic');
  const pollutantParams = liveParams.filter(p => p.category === 'pollutant');
  const envParams = liveParams.filter(p => p.category === 'environmental');
  const healthParams = liveParams.filter(p => p.category === 'health');

  // ── Sensor data fetching (replicates DeviceOverviewHighcharts logic) ───────
  useEffect(() => {
    if (!id || !deviceDetails?.deviceId) return;
    setChartData([]);

    // If device is inactive fetch around last updated
    if (deviceDetails.lastUpdated && !deviceDetails.isActive) {
      isFetchingRef.current = true;
      const from = new Date(new Date(deviceDetails.lastUpdated).setHours(new Date(deviceDetails.lastUpdated).getHours() - 12));
      const to = new Date(deviceDetails.lastUpdated);
      setCustomFromDate(getLocalDateTimeString(from));
      setCustomToDate(getLocalDateTimeString(to));
      setUserSelectedTimeRange('custom');
      getDeviceSensorData({ DeviceId: Number(id), FromDate: from.toISOString(), ToDate: to.toISOString() })
        .finally(() => { isFetchingRef.current = false; });
    }

    return () => { setUserSelectedTimeRange('12h'); };
  }, []);

  useEffect(() => {
    if (!id || userSelectedTimeRange === '12h') return;
    if (isFetchingRef.current) return;
    setChartData([]);
    isFetchingRef.current = true;

    let from: string;
    const to: string = new Date().toISOString();

    if (userSelectedTimeRange === '24h') from = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    else if (userSelectedTimeRange === '7d') from = new Date(Date.now() - 7 * 86400 * 1000).toISOString();
    else if (userSelectedTimeRange === '30d') from = new Date(Date.now() - 30 * 86400 * 1000).toISOString();
    else if (userSelectedTimeRange === 'custom') {
      from = new Date(customFromDate).toISOString();
      getDeviceSensorData({ DeviceId: Number(id), FromDate: from, ToDate: new Date(customToDate).toISOString() })
        .finally(() => { isFetchingRef.current = false; });
      return;
    }
    else from = new Date(Date.now() - 12 * 3600 * 1000).toISOString();

    getDeviceSensorData({ DeviceId: Number(id), FromDate: from, ToDate: to })
      .finally(() => { isFetchingRef.current = false; });
  }, [userSelectedTimeRange, customDateChanged]);

  // Poll live on 12h tab
  useEffect(() => {
    if (!id || !deviceDetails?.deviceId) return;
    if (deviceSensorData && deviceSensorData.deviceId.toString() === id &&
        deviceDetails.lastUpdated === deviceSensorData.readings[deviceSensorData.readings.length - 1]?.recordedAt &&
        deviceDetails.isActive) return;
    if (userSelectedTimeRange !== '12h') return;
    if (isFetchingRef.current) return;

    setChartData([]);
    isFetchingRef.current = true;
    getDeviceSensorData({
      DeviceId: Number(id),
      FromDate: new Date(Date.now() - 12 * 3600 * 1000).toISOString(),
      ToDate: new Date().toISOString(),
    }).finally(() => { isFetchingRef.current = false; });
  }, [deviceDetails, userSelectedTimeRange]);

  // ── Selected parameter for chart (default AQI) ────────────────────────────
  // Mirrors the old DeviceOverviewHighcharts approach: always ensure the selected
  // parameter is valid for the current device. If the context carries a stale
  // driverID from a previous device, reset it to AQI (or first available param).
  useEffect(() => {
    if (!parametersForDevice.length) return;
    const validIds = parametersForDevice.map(p => p.driverID);
    if (userSelectedParameterId !== 0 && validIds.includes(userSelectedParameterId)) return;
    const aqiParam = parametersForDevice.find(p => p.parameterName === 'AQI');
    setUserSelectedParameterId((aqiParam ?? parametersForDevice[0]).driverID);
    setUserSelectedParameterId2(0);
  }, [parametersForDevice]);

  // ── Fill missing data intervals ───────────────────────────────────────────
  const fillMissingIntervals = (data: DeviceReading[], intervalMs: number): any[] => {
    if (!data || data.length === 0) return [];
    const startTime = new Date(data[0].recordedAt).getTime();
    const endTime = new Date(data[data.length - 1].recordedAt).getTime();
    const filledData: any[] = [];
    const dataMap = new Map<string, DeviceReading>();
    data.forEach(item => dataMap.set(new Date(item.recordedAt).toISOString(), item));

    for (let t = startTime; t <= endTime; t += intervalMs) {
      const iso = new Date(t).toISOString();
      if (dataMap.has(iso)) {
        filledData.push({ ...dataMap.get(iso)!, recordedAt: iso });
      } else {
        filledData.push({ ...data[0], recordedAt: iso, parameterValue: null, subIndex: null });
      }
    }
    return filledData;
  };

  // ── Build chart data from sensor readings ─────────────────────────────────
  useEffect(() => {
    if (!deviceSensorData?.readings?.length) return;
    const selectedId = userSelectedParameterId;
    let baseData = deviceSensorData.readings.filter(d => d.parameterDriverId === selectedId);
    if (baseData.length === 0) return;

    let interval = 5 * 60 * 1000;
    if (userSelectedTimeRange === '24h') interval = 3600 * 1000;
    else if (userSelectedTimeRange === '7d' || userSelectedTimeRange === '30d') interval = 86400 * 1000;
    else if (userSelectedTimeRange === 'custom') {
      const diff = getDifferenceInHours(new Date(customFromDate), new Date(customToDate));
      interval = diff <= 12 ? 5 * 60 * 1000 : diff <= 24 ? 3600 * 1000 : 86400 * 1000;
    }

    baseData = fillMissingIntervals(baseData, interval);

    if (userSelectedParameterId2 === 0) {
      setChartData(baseData);
      return;
    }

    let secondData = deviceSensorData.readings.filter(d => d.parameterDriverId === userSelectedParameterId2);
    secondData = fillMissingIntervals(secondData, interval);

    const merged = baseData.map(d => {
      const match = secondData.find(s => s.recordedAt === d.recordedAt);
      return {
        ...d,
        parameterValue2: match?.parameterValue ?? null,
        parameterName2: match?.parameterName ?? null,
        parameterDisplayName2: match?.parameterDisplayName ?? null,
        preferredUnitName2: match?.preferredUnitName ?? null,
        subIndex2: match?.subIndex ?? null,
      };
    });
    setChartData(merged);
  }, [userSelectedParameterId, userSelectedParameterId2, deviceSensorData, userSelectedTimeRange]);

  // ── Helper: get parameter color for chart point ───────────────────────────
  const getParamColor = (value: number, subIndex: number, parameterName: string) => {
    const useSub = ['PM2.5', 'PM10', 'O₃', 'CO', 'NO₂'].includes(parameterName) && subIndex != null;
    return HelperFunctions.GetParameterColor(useSub ? subIndex : value, useSub ? 'AQI' : parameterName);
  };

  // ── Main pollutant at a given timestamp ───────────────────────────────────
  const getMainPollutantAtTime = (recordedAt: string) => {
    const readings = deviceSensorData?.readings?.filter(
      d => new Date(d.recordedAt).toISOString() === recordedAt
    );
    if (!readings || readings.length === 0) return '';
    const max = readings.reduce((a, b) => (b.subIndex > a.subIndex ? b : a));
    return max?.parameterName || '';
  };

  // ── Highcharts options ────────────────────────────────────────────────────
  const chartOptions = useMemo((): Highcharts.Options => {
    const isDark = theme === 'dark';
    const gridColor = isDark ? '#2D2D2D' : '#E5E7EB';
    const textColor = isDark ? '#9CA3AF' : '#6B7280';
    const bgColor = isDark ? '#1F2937' : '#FFFFFF';
    const tooltipBg = isDark ? '#374151' : '#FFFFFF';

    if (!chartData.length || !chartData[0]) {
      return {
        chart: { backgroundColor: bgColor, height: 280 },
        title: { text: '' },
        series: [],
      };
    }

    const yValues = chartData.map(d => d.parameterValue).filter(v => v != null);
    const yMax = yValues.length ? Math.max(...yValues) : 100;
    const yMin = yValues.length ? Math.min(...yValues) : 0;
    const allSame = yMin === yMax;
    const hasSinglePt = chartData.length === 1;

    const showSecond = userSelectedParameterId2 !== 0 && chartData[0]?.parameterValue2 !== undefined;

    // Primary series color
    const primaryColor = showSecond
      ? '#FF8C00'
      : allSame
        ? getParamColor(chartData[0].parameterValue, chartData[0].subIndex, chartData[0].parameterName)
        : {
            linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
            stops: chartData.map((d, i) => [
              i / Math.max(chartData.length - 1, 1),
              getParamColor(d.parameterValue, d.subIndex, d.parameterName),
            ]),
          };

    const series: Highcharts.SeriesOptionsType[] = [
      {
        type: 'line',
        name: 'Overall',
        data: chartData.map(d => ({
          x: new Date(d.recordedAt).getTime(),
          y: d.parameterValue,
        })),
        color: primaryColor as any,
        lineWidth: hasSinglePt ? 0 : 2,
        connectNulls: true,
        marker: { enabled: hasSinglePt, radius: hasSinglePt ? 6 : 0, states: { hover: { enabled: true, radius: 6 } } },
        yAxis: 0,
      } as Highcharts.SeriesLineOptions,
    ];

    if (showSecond) {
      const secondParamName = parametersForDevice.find(p => p.driverID === userSelectedParameterId2)?.parameterName || 'Parameter 2';
      series.push({
        type: 'line',
        name: secondParamName,
        data: chartData.map(d => ({ x: new Date(d.recordedAt).getTime(), y: d.parameterValue2 })),
        color: '#005EE5',
        lineWidth: 2,
        connectNulls: true,
        marker: { enabled: false, states: { hover: { enabled: true, radius: 6 } } },
        yAxis: 1,
      } as Highcharts.SeriesLineOptions);
    }

    // Threshold plot line at AQI 200
    const plotLines: Highcharts.YAxisPlotLinesOptions[] = showThreshold
      ? [{ value: 200, color: '#FF8880', width: 1.5, dashStyle: 'Dash', label: { text: '200', align: 'right', style: { color: '#FF8880', fontSize: '10px' } } }]
      : [];

    return {
      chart: {
        backgroundColor: bgColor,
        height: 280,
        style: { fontFamily: 'inherit' },
        animation: true,
        spacingTop: 10,
        spacingBottom: 10,
      },
      title: { text: '' },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        type: 'datetime',
        lineColor: gridColor,
        tickColor: gridColor,
        labels: { style: { color: textColor, fontSize: '10px' }, format: '{value:%H:%M}' },
        crosshair: { color: isDark ? '#4B5563' : '#D1D5DB', dashStyle: 'Dot', width: 1 },
      },
      yAxis: [
        {
          title: { text: 'AQI (μg/m³)', style: { color: textColor, fontSize: '10px' } },
          labels: { style: { color: textColor, fontSize: '10px' } },
          gridLineColor: gridColor,
          min: 0,
          plotLines,
        },
        {
          title: { text: 'CO₂ (ppm)', style: { color: textColor, fontSize: '10px' } },
          labels: { style: { color: textColor, fontSize: '10px' } },
          gridLineColor: 'transparent',
          opposite: true,
          min: 0,
        },
      ],
      tooltip: {
        backgroundColor: tooltipBg,
        borderColor: isDark ? '#4B5563' : '#E5E7EB',
        borderRadius: 8,
        style: { color: isDark ? '#F9FAFB' : '#111827', fontSize: '12px' },
        shared: false,
        useHTML: true,
        formatter: function () {
          const self = this as any;
          const d = chartData[self.point.index];
          if (!d) return '';
          const dt = new Date(d.recordedAt).toLocaleString('en-US', { ...options, timeZone });
          const statusText = d.parameterName
            ? HelperFunctions.GetStatusText(
                ['PM2.5','PM10','CO','O₃','NO₂'].includes(d.parameterName) && d.subIndex != null ? d.subIndex : d.parameterValue,
                ['PM2.5','PM10','CO','O₃','NO₂'].includes(d.parameterName) && d.subIndex != null ? 'AQI' : d.parameterName
              )
            : '';
          const mainPollutant = getMainPollutantAtTime(new Date(d.recordedAt).toISOString());
          const colorDot = `<span style="color:${statusBgColor(statusText)}">●</span>`;
          return `
            <div style="padding:4px 2px">
              <div style="font-size:11px;color:${textColor};margin-bottom:4px">${dt}</div>
              <div style="font-weight:600">${colorDot} AQI: ${d.parameterValue?.toFixed(0) ?? '--'}</div>
              ${mainPollutant ? `<div style="font-size:11px">Main pollutant: <b>${mainPollutant}</b></div>` : ''}
              ${statusText ? `<div style="font-size:11px;color:${statusBgColor(statusText)}">${statusText}</div>` : ''}
            </div>`;
        },
      },
      plotOptions: {
        line: { states: { hover: { lineWidth: 3 } } },
        series: { animation: { duration: 800 } },
      },
      series,
    };
  }, [chartData, theme, showThreshold, userSelectedParameterId2, parametersForDevice, timeZone]);

  // ── Parameter summary table ───────────────────────────────────────────────
  interface SummaryRow { name: string; min: number | null; max: number | null; avg: number | null; trend: string; }
  const parameterSummary: SummaryRow[] = useMemo(() => {
    if (!deviceSensorData?.readings?.length) return [];

    const byParam = new Map<string, number[]>();
    deviceSensorData.readings.forEach(r => {
      if (!byParam.has(r.parameterName)) byParam.set(r.parameterName, []);
      if (r.parameterValue != null) byParam.get(r.parameterName)!.push(r.parameterValue);
    });

    const result: SummaryRow[] = [];
    byParam.forEach((vals, name) => {
      if (vals.length === 0) return;
      const mn = Math.min(...vals);
      const mx = Math.max(...vals);
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      const last = vals[vals.length - 1];
      const prev = vals.length > 1 ? vals[vals.length - 2] : avg;
      const trend = last > prev * 1.02 ? 'Rising' : last < prev * 0.98 ? 'Improving' : 'Stable';
      result.push({ name, min: parseFloat(mn.toFixed(1)), max: parseFloat(mx.toFixed(1)), avg: parseFloat(avg.toFixed(1)), trend });
    });

    // Show only key params first
    const priority = ['AQI', 'PM2.5', 'CO₂', 'CO2', 'TVOC', 'Temperature'];
    result.sort((a, b) => {
      const ai = priority.findIndex(p => a.name.includes(p) || p.includes(a.name));
      const bi = priority.findIndex(p => b.name.includes(p) || p.includes(b.name));
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    return result.slice(0, 6);
  }, [deviceSensorData]);

  // ── Export handler ────────────────────────────────────────────────────────
  const handleExport = async (fileType: string) => {
    if (!deviceDetails || !id || !deviceSensorData?.readings?.length) {
      alert('No data available to download for the selected date range. Please try a different period.');
      return;
    }
    try {
      await exportDeviceData(id, fileType, deviceDetails.serialNumber || '', deviceDetails.deviceName || '');
      setShowExportDropdown(false);
    } catch (e) {
      console.error('Export failed', e);
    }
  };

  // ── Delete handler ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const res = await deleteDevice(id as string);
      if (res.success) {
        setIsDeletingSuccess(true);
        setTimeout(() => { setShowDeleteModal(false); navigate('/devices'); }, 1500);
      }
    } catch (e) {
      console.error('Delete failed', e);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Custom date handler ───────────────────────────────────────────────────
  const handleCustomDateApply = () => {
    if (!customFromDate || !customToDate) {
      setCustomDateError('Select both dates');
      return;
    }
    const f = customFromDate.includes('T') ? customFromDate.split('T')[0] : customFromDate;
    const t = customToDate.includes('T') ? customToDate.split('T')[0] : customToDate;
    if (f >= t) { setCustomDateError('Start must be before end'); return; }
    setCustomDateError('');
    setCustomDateChanged(v => !v);
    setShowTimeRangeDropdown(false);
  };

  // ── Inactive warning dismiss ───────────────────────────────────────────────
  const handleInActiveWarning = () => {
    const el = document.querySelector('.inActive-warning-container');
    el?.classList.add('removeInActiveSlider');
    setTimeout(() => setShowWarning(false), 500);
  };

  // ── Redirect if no id ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!id && devicesForDashboard.length > 0) {
      navigate(`/new-device-design/${devicesForDashboard[0].deviceId}`, { replace: true });
    }
  }, [id, devicesForDashboard]);

  // ── Chart legend items ────────────────────────────────────────────────────
  const selectedParam2Name = userSelectedParameterId2 !== 0
    ? parametersForDevice.find(p => p.driverID === userSelectedParameterId2)?.parameterName || ''
    : '';

  // ── Legend color swatches for the chart ──────────────────────────────────
  const legendColors = [
    { label: '0–50', color: '#A0ED9F' },
    { label: '51–150', color: '#FFE48A' },
    { label: 'Above 150', color: '#FF8880' },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  if (!id) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full text-[var(--color-text)]">
          <p>Loading devices…</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {showConfigFlow && !isRNApp() && !isMobile && deviceDetails?.serialNumber ? (
        <DeviceConfigFlowWeb
          setShowConfigFlow={setShowConfigFlow}
          SerialNumber={deviceDetails.serialNumber}
          deviceId={id}
        />
      ) : (
        <>
      <div className="flex flex-col h-full min-h-0 text-[var(--color-text)] bg-[var(--modal-background)] overflow-auto">

        {/* ── Top Header ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-4 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700">
          {/* Left: back + device info */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate('/devices')}
              className="w-9 h-9 grid place-items-center rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-[var(--color-text)] flex-shrink-0"
              style={{ transform: 'translateY(-1px)' }}
              aria-label="Go back"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
            </button>

            {/* Device thumbnail */}
            <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
              <img
                src={deviceDetails?.deviceImageUrl || `${process.env.PUBLIC_URL}/SVGIcons/Sign In.png`}
                alt="Device"
                className="w-full h-full object-contain"
              />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base leading-[27px] font-semibold text-[var(--color-text)] truncate">
                  {deviceDetails?.deviceName || 'Device'}
                </h1>
                {(() => {
                  const { label, variant } = getDeviceStatusLabel(
                    deviceDetails?.isActive ?? false,
                    deviceDetails?.operationalStatus,
                  );
                  const colorClass =
                    variant === 'active'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : variant === 'initializing'
                        ? 'bg-[rgb(255,179,110)] text-white dark:bg-[rgb(255,179,110)]/80 dark:text-white'
                        : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
                  return (
                    <span
                      className={`text-[10px] sm:text-xs leading-[14px] sm:leading-4 font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${colorClass}`}
                    >
                      {label}
                    </span>
                  );
                })()}
              </div>
              <p className="text-xs leading-[18px] text-gray-500 dark:text-gray-400 mt-0.5">
                <span className="text-[var(--color-text)]">Device Model: </span><span className="font-semibold text-[var(--color-text)]">{deviceDetails?.deviceModel || '--'}</span>
                {connectivityText && (
                  <><span className="mx-1"></span><span className="text-[var(--color-text)]">Connectivity: </span><span className="font-semibold text-[var(--color-text)]">{connectivityText}</span></>
                )}
                {deviceDetails?.installationLocation && (
                  <><span className="mx-1"></span><span className="text-[var(--color-text)]">Site: </span><span className="font-semibold text-[var(--color-text)]">{deviceDetails.installationLocation}</span></>
                )}
              </p>
            </div>
          </div>

          {/* Right: AQI value + action icons */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {aqiValue != null && (
              <div className="text-right">
                <span className="text-2xl font-bold" style={{ color: aqiColor }}>
                  {Math.round(aqiValue)}
                </span>
                <span className="text-xs font-medium ml-1" style={{ color: aqiColor }}>
                  {aqiStatus !== 'NA' ? aqiStatus : ''}
                </span>
              </div>
            )}

            {/* Desktop action buttons — hidden on mobile */}
            {!isMobile && (
              <>
                <button
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Update Offsets"
                  onClick={() => setIsUpdateOffsetsModalOpen(true)}
                >
                  <Settings className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </button>
                {user?.username !== 'Demo2' && (
                  <button
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="About AQI"
                    onClick={() => setShowAQIModal(true)}
                  >
                    <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </button>
                )}
                {/* Desktop export dropdown */}
                <div className="relative" ref={exportDropdownRef}>
                  <button
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Export"
                    onClick={() => setShowExportDropdown(v => !v)}
                  >
                    <Download className="w-4 h-4 text-[var(--color-terms-link)]" />
                  </button>
                  {showExportDropdown && !isMobile && (
                    <div className="absolute right-0 top-10 z-50 bg-[var(--modal-background)] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 min-w-[120px]">
                      {ExportDataTypes.filter(t => t.value === 'excel' || t.value === 'csv').map(t => (
                        <button
                          key={t.value}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-[var(--color-text)]"
                          onClick={() => handleExport(t.value)}
                        >
                          <img src={`${process.env.PUBLIC_URL}/SVGIcons/${t.label}.svg`} alt={t.label} className="w-4 h-4" />
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Mobile three-dot menu trigger */}
            {isMobile && (
              <button
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setShowOtherOptions(true)}
                aria-label="More options"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="5" r="1" />
                  <circle cx="12" cy="19" r="1" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Environment Mode Tabs ───────────────────────────────────────── */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mr-2">
            Environment Mode
          </span>
          {ENV_MODES.map((mode, idx) => (
            <button
              key={mode}
              onClick={() => setSelectedEnvMode(idx)}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                selectedEnvMode === idx
                  ? 'bg-blue-500 dark:bg-blue-600 text-white border border-blue-500 dark:border-blue-600'
                  : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <img
                src={ENV_MODE_ICONS[mode] ?? `${process.env.PUBLIC_URL}/SVGIcons/default-env.svg`}
                alt={`${mode} icon`}
                className="w-3 h-3 object-contain"
              />
              <span>{mode}</span>
            </button>
          ))}
        </div>

        {/* ── View Toggle + Last Updated ──────────────────────────────────── */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap gap-2">
          <div className="flex gap-0 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
            {(['overview', 'heatmap'] as const).map(v => (
              <button
                key={v}
                onClick={() => toggleView(v)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors capitalize ${
                  viewMode === v
                    ? 'bg-white dark:bg-gray-600 text-[var(--color-text)] shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-[var(--color-text)]'
                }`}
              >
                {v === 'overview' ? 'Overview' : 'Heat Map'}
              </button>
            ))}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {deviceDetails?.lastUpdated
              ? new Date(deviceDetails.lastUpdated).toLocaleString('en-US', { timeZone, ...options })
              : 'N/A'}
          </span>
        </div>

        {/* ── Main Content ────────────────────────────────────────────────── */}
        {viewMode === 'overview' ? (
          <div className={`flex flex-1 min-h-0 overflow-hidden ${isMobile ? 'flex-col' : 'flex-row'}`}>

            {/* ── Left Panel: AQI & Pollutants ─────────────────────────── */}
            <div
              className={`flex-shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto ${
                isMobile ? 'w-full border-r-0 border-b' : 'w-[320px]'
              }`}
            >
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-[var(--modal-background)] z-10">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  AQI &amp; Pollutants
                </span>
                <span className="flex items-center gap-1 text-xs font-semibold text-green-600 dark:text-green-400">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse inline-block" />
                  Live Data
                </span>
              </div>

              <div className="px-3 py-1">
                {/* AQI & Basic Pollutants */}
                <CollapsibleSection title="AQI &amp; Basic Pollutants" defaultOpen={true} titleColor="text-orange-500">
                  {basicParams.length > 0 ? basicParams.map(p => (
                    <ParamRow
                      key={p.driverID}
                      name={p.name}
                      displayName={p.displayName}
                      value={p.value}
                      unit={p.unit}
                      subIndex={p.subIndex}
                      isMainPollutant={p.name === mainPollutantName}
                      isPrimarySelected={userSelectedParameterId === p.driverID}
                      isSecondarySelected={userSelectedParameterId2 === p.driverID}
                      onSelect={() => handleParameterClick(p.driverID)}
                    />
                  )) : (
                    <p className="text-xs text-gray-400 py-2 px-1">No data available</p>
                  )}
                </CollapsibleSection>

                {/* Other Pollutants */}
                {pollutantParams.length > 0 && (
                  <CollapsibleSection title="Other Pollutants" titleColor="text-orange-500">
                    {pollutantParams.map(p => (
                      <ParamRow
                        key={p.driverID}
                        name={p.name}
                        displayName={p.displayName}
                        value={p.value}
                        unit={p.unit}
                        subIndex={p.subIndex}
                        isPrimarySelected={userSelectedParameterId === p.driverID}
                        isSecondarySelected={userSelectedParameterId2 === p.driverID}
                        onSelect={() => handleParameterClick(p.driverID)}
                      />
                    ))}
                  </CollapsibleSection>
                )}

                {/* Environmental Parameters */}
                {envParams.length > 0 && (
                  <CollapsibleSection title="Environmental Parameters" titleColor="text-blue-500">
                    {envParams.map(p => (
                      <ParamRow
                        key={p.driverID}
                        name={p.name}
                        displayName={p.displayName}
                        value={p.value}
                        unit={p.unit}
                        subIndex={p.subIndex}
                        isPrimarySelected={userSelectedParameterId === p.driverID}
                        isSecondarySelected={userSelectedParameterId2 === p.driverID}
                        onSelect={() => handleParameterClick(p.driverID)}
                      />
                    ))}
                  </CollapsibleSection>
                )}

                {/* Health & Risk Indicators */}
                {healthParams.length > 0 && (
                  <CollapsibleSection title="Health &amp; Risk Indicators" titleColor="text-purple-500">
                    {healthParams.map(p => (
                      <ParamRow
                        key={p.driverID}
                        name={p.name}
                        displayName={p.displayName}
                        value={p.value}
                        unit={p.unit}
                        subIndex={p.subIndex}
                        isPrimarySelected={userSelectedParameterId === p.driverID}
                        isSecondarySelected={userSelectedParameterId2 === p.driverID}
                        onSelect={() => handleParameterClick(p.driverID)}
                      />
                    ))}
                  </CollapsibleSection>
                )}
              </div>

            </div>

            {/* ── Right Panel ──────────────────────────────────────────── */}
            <div className="flex-1 flex flex-col overflow-y-auto min-w-0 p-4 gap-4">

              {/* ── Detailed Insight Trend Chart ─────────────────────── */}
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Chart header */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Detailed Insight Trend
                  </span>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Threshold toggle */}
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showThreshold}
                        onChange={e => setShowThreshold(e.target.checked)}
                        className="w-3.5 h-3.5 accent-green-600"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">Threshold</span>
                    </label>

                    {/* Time range dropdown */}
                    <div className="relative" ref={timeRangeDropdownRef}>
                      <button
                        className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-xs text-[var(--color-text)] hover:bg-gray-50 dark:hover:bg-gray-700"
                        onClick={() => setShowTimeRangeDropdown(v => !v)}
                      >
                        <span>{timeRanges.find(r => r.value === userSelectedTimeRange)?.label || 'Last 12 Hours'}</span>
                        <ChevronDown className="w-3 h-3" />
                      </button>

                      {showTimeRangeDropdown && (
                        <div className="absolute right-0 top-8 z-50 bg-[var(--modal-background)] border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 min-w-[160px]">
                          {timeRanges.map(r => (
                            <button
                              key={r.value}
                              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 ${userSelectedTimeRange === r.value ? 'font-semibold text-[var(--color-terms-link)]' : 'text-[var(--color-text)]'}`}
                              onClick={() => {
                                setUserSelectedTimeRange(r.value);
                                if (r.value !== 'custom') setShowTimeRangeDropdown(false);
                              }}
                            >
                              {r.label}
                            </button>
                          ))}
                          {userSelectedTimeRange === 'custom' && (
                            <div className="px-3 pb-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                              <div className="flex flex-col gap-1 mb-2">
                                <input
                                  type="datetime-local"
                                  value={customFromDate}
                                  onChange={e => setCustomFromDate(e.target.value)}
                                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-[var(--input-background-color)] text-[var(--color-text)]"
                                />
                                <input
                                  type="datetime-local"
                                  value={customToDate}
                                  onChange={e => setCustomToDate(e.target.value)}
                                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-[var(--input-background-color)] text-[var(--color-text)]"
                                />
                                {customDateError && <p className="text-red-500 text-xs">{customDateError}</p>}
                              </div>
                              <button
                                className="w-full text-xs bg-[var(--gradient-button-left-color)] text-white rounded px-2 py-1"
                                onClick={handleCustomDateApply}
                              >
                                Apply
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Chart */}
                <div className="px-2 pt-2 bg-[var(--modal-background)]">
                  {chartData.length > 0 ? (
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                  ) : (
                    <div className="flex items-center justify-center h-[280px] text-sm text-gray-400 dark:text-gray-500">
                      {!deviceSensorData ? 'Loading chart data…' : 'No data for selected range'}
                    </div>
                  )}
                </div>

                {/* Legend */}
                <div className="px-4 pb-3 flex flex-wrap gap-x-4 gap-y-1 items-center">
                  {/* Overall legend */}
                  <span className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                    <span className="font-semibold">Overall AQI</span>
                  </span>
                  {legendColors.map(lc => (
                    <span key={lc.label} className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <span className="w-4 h-1.5 rounded-full inline-block" style={{ backgroundColor: lc.color }} />
                      {lc.label}
                    </span>
                  ))}
                  {userSelectedParameterId2 !== 0 && selectedParam2Name && (
                    <>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <span className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                        <span className="w-4 h-1.5 rounded-full inline-block bg-blue-600" />
                        <span className="font-semibold">{selectedParam2Name}</span>
                      </span>
                      {legendColors.map(lc => (
                        <span key={`s2-${lc.label}`} className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <span className="w-4 h-1.5 rounded-full inline-block" style={{ backgroundColor: lc.color }} />
                          {lc.label}
                        </span>
                      ))}
                    </>
                  )}
                </div>

              </div>

              {/* ── Bottom row: Parameter Summary + Ambient vs Indoor ─── */}
              <div className={`flex gap-4 ${isMobile ? 'flex-col' : 'flex-row items-start'}`}>

                {/* Parameter Summary Table */}
                <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden min-w-0">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Parameter Summary
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-700">
                          {['Parameter', 'Min', 'Max', 'Avg', 'Trend'].map(h => (
                            <th key={h} className="text-left px-3 py-2 text-gray-500 dark:text-gray-400 font-semibold whitespace-nowrap">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {parameterSummary.length > 0 ? parameterSummary.map(row => (
                          <tr key={row.name} className="border-b border-gray-50 dark:border-gray-700/50 last:border-b-0">
                            <td className="px-3 py-2 font-medium text-[var(--color-text)] whitespace-nowrap">{row.name}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.min ?? '--'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.max ?? '--'}</td>
                            <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.avg ?? '--'}</td>
                            <td className="px-3 py-2"><TrendIcon trend={row.trend} /></td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-gray-400 dark:text-gray-500">
                              No data yet — select a time range to load chart data.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Ambient vs Indoor */}
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex-shrink-0 w-full md:w-[280px]">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Ambient vs Indoor
                    </span>
                  </div>
                  <div className="p-3 flex flex-col gap-3">
                    {/* Info box */}
                    <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-lg px-3 py-2">
                      <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">
                        Connect ambient sensor to enable outdoor vs indoor AQI comparison.
                      </p>
                    </div>

                    {/* Indoor + Ambient cards */}
                    <div className="flex gap-2">
                      {/* Indoor AQI */}
                      <div className="flex-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/50 p-3">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mb-1">Indoor AQI</p>
                        <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                          {aqiValue != null ? Math.round(aqiValue) : '--'}
                        </p>
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">Current</p>
                      </div>

                      {/* Ambient AQI */}
                      <div className="flex-1 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600 p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">Ambient AQI</p>
                        <p className="text-3xl font-bold text-gray-400 dark:text-gray-500">---</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Not Connected</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ── Heat Map View ─────────────────────────────────────────────── */
          <div className="flex-1 p-4 overflow-auto">
            <DeviceHeatMap
              deviceId={deviceDetails?.deviceId?.toString() || ''}
              deviceName={deviceDetails?.deviceName || ''}
              viewMode={heatmapTab}
              setViewMode={toggleTab}
            />
          </div>
        )}
      </div>

      {/* ── Inactive device warning banner (mobile only) ─────────────────── */}
      {isMobile && !deviceDetails?.isActive && deviceDetails?.lastUpdated && showWarning && (
        <div className="inActive-warning-container">
          <AlertTriangle className="inActive-warning-icon" />
          <div className="inActive-warning-info">
            <h3 className="inActive-warning-title">Device Offline</h3>
            <p className="inActive-warning-subtitle">
              Updated on{' '}
              {new Date(deviceDetails.lastUpdated).toLocaleString('en-US', { timeZone, ...options })}
            </p>
          </div>
          <button
            className="inActive-warning-close-btn"
            onClick={handleInActiveWarning}
            aria-label="Dismiss warning"
          >
            <X className="inActive-warning-remove-icon" />
          </button>
        </div>
      )}

      {/* ── Mobile three-dot action sheet ────────────────────────────────── */}
      {showOtherOptions && isMobile && (
        <div className="mobile-action-menu-overlay" onClick={() => setShowOtherOptions(false)}>
          <div className="mobile-action-menu" onClick={e => e.stopPropagation()}>
            <button className="mobile-action-close" onClick={() => setShowOtherOptions(false)}>
              <X className="w-5 h-5 text-[var(--color-text)]" />
            </button>
            <div className="mobile-action-content">
              {user?.username !== 'Demo2' && (
                <button
                  className="mobile-action-item"
                  onClick={() => { setShowOtherOptions(false); setShowAQIModal(true); }}
                >
                  <Info className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                  About AQI
                </button>
              )}
              <button
                className="mobile-action-item"
                onClick={() => { setShowOtherOptions(false); setShowExportDropdown(true); }}
              >
                <Download className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                Export Data
              </button>
              <button
                className="mobile-action-item"
                onClick={() => { setShowOtherOptions(false); navigate(`/edit-device/${id}`); }}
              >
                <Pencil className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                Edit Device
              </button>
              <button
                className="mobile-action-item"
                onClick={() => { setShowOtherOptions(false); setIsUpdateOffsetsModalOpen(true); }}
              >
                <Settings className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                Update Offsets
              </button>
              <button
                className="mobile-action-item"
                onClick={() => { setShowOtherOptions(false); setShowConfigFlow(true); }}
              >
                <Settings className="w-5 h-5 text-[var(--color-sub-title-text)]" />
                Edit Config
              </button>
              <button
                className="mobile-action-item text-[#E67E7D]"
                onClick={() => { setShowOtherOptions(false); setShowDeleteModal(true); }}
              >
                <Trash2 className="w-5 h-5 text-[#E67E7D]" />
                Delete Device
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mobile export bottom sheet ────────────────────────────────────── */}
      {showExportDropdown && isMobile && (
        <div className="mobile-action-menu-overlay" onClick={() => setShowExportDropdown(false)}>
          <div className="mobile-action-menu" onClick={e => e.stopPropagation()}>
            <button className="mobile-action-close" onClick={() => setShowExportDropdown(false)}>
              <X className="w-5 h-5 text-[var(--color-text)]" />
            </button>
            <div className="mobile-action-content">
              <p className="text-sm font-semibold text-[var(--color-text)] mb-1">Select Export Type</p>
              {ExportDataTypes.filter(t => t.value === 'excel' || t.value === 'csv').map(t => (
                <button
                  key={t.value}
                  className="mobile-action-item"
                  onClick={() => handleExport(t.value)}
                >
                  <img src={`${process.env.PUBLIC_URL}/SVGIcons/${t.label}.svg`} alt={t.label} className="w-5 h-5" />
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <AQILearningModal isOpen={showAQIModal} onClose={() => setShowAQIModal(false)} />

      <DeleteDeviceModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        isDeletingSuccess={isDeletingSuccess}
      />

      {isUpdateOffsetsModalOpen && (
        <DeviceOffSets
          isOpen={isUpdateOffsetsModalOpen}
          onClose={() => setIsUpdateOffsetsModalOpen(false)}
          deviceDetails={deviceDetails || ({} as DeviceDashboardDto)}
        />
      )}

      {showConfigFlow && (isRNApp() || isMobile) && deviceDetails?.serialNumber && (
        <DeviceConfigFlowMobile
          setShowConfigFlow={setShowConfigFlow}
          SerialNumber={deviceDetails.serialNumber}
          deviceId={id}
        />
      )}
        </>
      )}
    </DashboardLayout>
  );
};

export default NewDeviceDetailPage;
